import { useMemo, useState } from 'react';
import { Search, X, Check, Loader2 } from 'lucide-react';

export interface FacetOption {
  value: string;
  /** Primary display label (e.g. agency name, or NAICS code). */
  label: string;
  /** Optional secondary line (e.g. the NAICS title). */
  secondary?: string;
  /** Count of open opportunities for this value. */
  count?: number;
}

interface FacetMultiSelectProps {
  options: FacetOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading?: boolean;
  placeholder?: string;
  emptyText?: string;
  /** Cap how many options render before searching (keeps the list snappy). */
  maxVisible?: number;
}

/**
 * A searchable, multi-select facet picker. Used for the dashboard's smart Agency
 * and NAICS filters: options come from the live opportunity pool (with counts)
 * and the user searches by name/code/title. Selected values render as removable
 * chips above the list.
 */
export function FacetMultiSelect({
  options,
  selected,
  onChange,
  loading = false,
  placeholder = 'Search…',
  emptyText = 'No matches',
  maxVisible = 60,
}: FacetMultiSelectProps) {
  const [query, setQuery] = useState('');

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const byValue = useMemo(() => {
    const m = new Map<string, FacetOption>();
    for (const o of options) m.set(o.value, o);
    return m;
  }, [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? options.filter(
          (o) =>
            o.value.toLowerCase().includes(q) ||
            o.label.toLowerCase().includes(q) ||
            (o.secondary || '').toLowerCase().includes(q)
        )
      : options;
    return base.slice(0, maxVisible);
  }, [options, query, maxVisible]);

  const toggle = (value: string) => {
    if (selectedSet.has(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };

  return (
    <div className="space-y-3">
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((value) => {
            const opt = byValue.get(value);
            return (
              <span
                key={value}
                className="inline-flex items-center gap-1 px-3 py-1 bg-bw-primary-tint text-bw-primary text-sm rounded-full"
              >
                <span className="truncate max-w-[180px]">{opt?.label || value}</span>
                <button onClick={() => toggle(value)} className="hover:text-bw-primary" aria-label={`Remove ${value}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-bw-muted" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
        />
      </div>

      {/* Options */}
      <div className="max-h-56 overflow-y-auto border border-bw-border rounded-lg divide-y divide-bw-border/60">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-bw-muted text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-6 text-center text-bw-muted text-sm">{emptyText}</div>
        ) : (
          filtered.map((o) => {
            const isSel = selectedSet.has(o.value);
            return (
              <button
                key={o.value}
                onClick={() => toggle(o.value)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                  isSel ? 'bg-bw-primary-tint' : 'hover:bg-bw-surface-subtle'
                }`}
              >
                <span
                  className={`flex items-center justify-center h-4 w-4 rounded border flex-shrink-0 ${
                    isSel ? 'bg-bw-primary border-bw-primary text-white' : 'border-bw-border'
                  }`}
                >
                  {isSel && <Check className="h-3 w-3" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-bw-ink">{o.label}</span>
                  {o.secondary && (
                    <span className="block truncate text-xs text-bw-muted">{o.secondary}</span>
                  )}
                </span>
                {typeof o.count === 'number' && (
                  <span className="text-xs text-bw-muted flex-shrink-0">{o.count}</span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
